import React, { useEffect, useMemo, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  FeatureBase, 
  FeatureSource 
} from "@models";
import { CHALLENGE_RATINGS, RARITY_NUM_TO_STR_MAP, RARITY_OPTIONS } from "@dndModels/Constants";
import { 
  CharacterFeatureSource,
  CharacterSheetInterface, 
  Creature, 
  CreatureGift, 
  BasicCreatureInstance, 
  Item, 
  MagicItemKeyword, 
  StandardFeatures, 
  SummonInstance,
  Summon
} from "@dndModels";

import { makeStyles } from "@utilities/theme_utilities";

import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import AddButton from "@components/input/buttons/AddButton";

interface Props {
  feature_bases: FeatureBase[];
  feature_source: FeatureSource | StandardFeatures | Creature | CreatureGift | Summon;
  cfs?: CharacterSheetInterface | BasicCreatureInstance | SummonInstance | CharacterFeatureSource;
  creature_instances?: BasicCreatureInstance[];
  disabled?: boolean;
  onExpand: (feature_base: FeatureBase) => void;
  onAdd: () => void;
  level_filter?: number;
  onLevelFilterChange?: (changed: number) => void;
  simple?: boolean;
  height: number;
}

export default function FeatureBasesInput({
  feature_bases,
  feature_source,
  cfs,
  creature_instances,
  onExpand,
  onAdd,
  disabled = false,
  level_filter = -1,
  onLevelFilterChange,
  simple = false,
  height
}: Props) {
  const [use_level_filter, setUseLevelFilter] = useState(level_filter);
  
  const rarity_options = useMemo(() => {
    if (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) {
      return RARITY_OPTIONS.filter(o => +o.id !== -1 && +o.id >= feature_source.min_rarity && +o.id <= feature_source.max_rarity);
    }
    return [];
  }, [feature_source]);
  
  useEffect(() => {
    if (level_filter !== use_level_filter) {
      setUseLevelFilter(level_filter);
    }
  }, [level_filter, use_level_filter, setUseLevelFilter]);

  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        '&.tableHeader': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.secondary.dark,
        },
        '&.alternate': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.dark,
        },
      },
    });
  });

  const descriptionStyle = () => {
    const descWidth = Math.floor(window.innerWidth * 0.8);
  
    const properties: React.CSSProperties = {
      width: `${descWidth}px`,
      whiteSpace: "nowrap", 
      overflow: "hidden", 
      textOverflow: "ellipsis"
    } as React.CSSProperties;

    return properties;
  }

  const renderLevelSelect = () => {
    if (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) {
      // debugger;
      // console.log(rarity_options);
      return (
        <SelectStringBox  
          name="Filter for Rarity"
          options={rarity_options}
          extra_options={["All"]}
          value={ level_filter === -1 ? "All" : `${level_filter}`}
          // log_it
          onChange={(changed: string) => {
            if (onLevelFilterChange) {
              if (changed === "All") {
                onLevelFilterChange(-1);
              } else {
                onLevelFilterChange(+changed);
              }
            }
          }}
        />
      );
    } else {
      return (
        <SelectStringBox
          name="Filter for Level"
          options={CHALLENGE_RATINGS}
          extra_options={["All"]}
          value={ level_filter === -1 ? "All" : `${level_filter}`}
          onChange={(changed: string) => {
            if (onLevelFilterChange) {
              if (changed === "All") {
                onLevelFilterChange(-1);
              } else {
                onLevelFilterChange(+changed);
              }
            }
          }}
        />
      );
    }
  }
  
  const { classes, cx } = useStyles();
  // console.log(feature_bases);
  // console.log(disabled);
  // console.log(level_filter);
  // console.log(feature_bases.filter(o =>(!disabled || o.display) && (level_filter === -1 || o.level === level_filter)));
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={3}>
        <span className="header padded">
          Feature Bases
        </span>
        { !disabled &&
          <AddButton
            onClick={ () => {
              onAdd();
            }}
          />
        }
      </Grid>
      <Grid size={9}>
        { level_filter !== undefined && renderLevelSelect() }
      </Grid>
      <Grid size={12} className={cx(classes.root, "tableHeader")}
        container spacing={0} direction="row">
        <Grid size={2}>
          Name
        </Grid>
        <Grid size={1}>
          { (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) ?
            "Rarity"
          :
            "Level"
          }
        </Grid>
        <Grid size={9}>
          Description
        </Grid>
      </Grid>
      <Grid size={12}
        style={{ 
          height: height === 0 ? "inherit" : height - 40, // `calc(100vh - 83)`
          overflowY: "auto", 
          width: "100%", 
          overflowX: "hidden",
        }}>
        <Grid container spacing={0} direction="row">
          {/* <Grid item xs={12} */}
          { feature_bases.filter(o =>(!disabled || o.display) && (level_filter === -1 || o.level === level_filter)).sort((a, b) => { 
            if (a.level === b.level) {
              return a.name.localeCompare(b.name);
            } else {
              return +a.level < +b.level ? -1 : 1;
            }
          }).map((feature_base, key) => {
            const use_cfs = creature_instances ? creature_instances.find(o => 
              o.challenge_rating === feature_base.level) : cfs;
            const description = simple && feature_base.features.length === 1 ? feature_base.features[0].description : feature_base.description;
            return (
              <Grid size={12} key={feature_base.true_id}
                container spacing={0} direction="row"
                onClick={() => {
                  onExpand(feature_base);
                }}
                className={cx(classes.root, (key % 2 === 1 ? "alternate clickable" : "clickable" ))}>
                <Grid size={2}>
                  { feature_base.name === "" ? "Nameless Feature Base" : feature_base.name }
                </Grid>
                <Grid size={1}>
                  { (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) ?
                    RARITY_NUM_TO_STR_MAP[feature_base.level]
                  : feature_base.level }
                </Grid>
                <Grid size={9}>
                  <div style={descriptionStyle()}>
                    <DisplayStringWithReplacements display_string={description} cfs={use_cfs} />
                  </div>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
}
